import { useState, useEffect, useMemo } from "react";
import { api } from "../api";
import {
  Loader2, Calendar, Clock, Stethoscope, Building2, UserCircle,
  AlertTriangle, Filter, ArrowUpRight, ArrowDownRight, Users, Activity,
  Search, X
} from "lucide-react";

interface ScheduleData {
  total_h_semana: number | null;
  dias: number | null;
  blocos: number | null;
  weekdays: string | null;
  schedules: string | null;
  avg_occupancy: number | null;
}

interface StrategyData {
  consultas: number | null;
  vagas: number | null;
  vagas_livres: number | null;
  cirurgias: number | null;
  ocupacao: number | null;
  hours_per_week: number | null;
  diagnosis: string | null;
  conversion_rate: number | null;
}

interface Rules {
  occupancy_rule: string | null;
  low_hours_rule: string | null;
}

interface ComparisonRow {
  specialty: string;
  unit: string;
  doctor: string;
  doctor_id: number | null;
  schedule: ScheduleData;
  strategy: StrategyData;
  rules: Rules;
}

interface Filters {
  specialties: string[];
  units: string[];
  doctors: string[];
}

function fmtNum(n: number | null | undefined, digits = 0) {
  if (n === null || n === undefined) return "—";
  return n.toLocaleString("pt-PT", { maximumFractionDigits: digits });
}

function fmtPct(n: number | null | undefined) {
  if (n === null || n === undefined) return "—";
  return `${(n * 100).toFixed(1)}%`;
}

function fmtHours(n: number | null | undefined) {
  if (n === null || n === undefined) return "—";
  return `${n.toFixed(1)}h`;
}

function occupancyColor(n: number | null | undefined) {
  if (n === null || n === undefined) return "text-slate-500";
  if (n >= 0.75) return "text-emerald-600 font-semibold";
  if (n >= 0.50) return "text-amber-600";
  return "text-red-600";
}

function occupancyBadge(n: number | null | undefined) {
  if (n === null || n === undefined) return null;
  if (n >= 0.75) return "bg-emerald-50 text-emerald-700 border-emerald-200";
  if (n >= 0.50) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-red-50 text-red-700 border-red-200";
}

export default function ScheduleStrategy({ embedded }: { embedded?: boolean }) {
  const [rows, setRows] = useState<ComparisonRow[]>([]);
  const [filters, setFilters] = useState<Filters>({ specialties: [], units: [], doctors: [] });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [specialty, setSpecialty] = useState("");
  const [unit, setUnit] = useState("");
  const [doctor, setDoctor] = useState("");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"ocupacao" | "horas" | "consultas" | "vagas_livres" | "avg_occupancy">("ocupacao");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selectedRow, setSelectedRow] = useState<ComparisonRow | null>(null);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.getDoctorSchedulesComparison(),
      api.getStrategyFilters(),
    ])
      .then(([data, f]) => {
        setRows(data.rows || []);
        setFilters({
          specialties: f.specialties || [],
          units: f.units || [],
          doctors: f.doctors || [],
        });
      })
      .catch((err) => setError(err.message || "Failed to load data"))
      .finally(() => setLoading(false));
  }, []);

  const filteredRows = useMemo(() => {
    let out = rows;
    if (specialty) out = out.filter((r) => r.specialty === specialty);
    if (unit) out = out.filter((r) => r.unit === unit);
    if (doctor) out = out.filter((r) => r.doctor === doctor);
    if (search.trim()) {
      const s = search.toLowerCase();
      out = out.filter(
        (r) =>
          r.doctor.toLowerCase().includes(s) ||
          r.unit.toLowerCase().includes(s) ||
          r.specialty.toLowerCase().includes(s) ||
          (r.strategy.diagnosis || "").toLowerCase().includes(s)
      );
    }
    out = [...out].sort((a, b) => {
      let va: number | null = null;
      let vb: number | null = null;
      switch (sortBy) {
        case "ocupacao":
          va = a.strategy.ocupacao;
          vb = b.strategy.ocupacao;
          break;
        case "horas":
          va = a.schedule.total_h_semana;
          vb = b.schedule.total_h_semana;
          break;
        case "consultas":
          va = a.strategy.consultas;
          vb = b.strategy.consultas;
          break;
        case "vagas_livres":
          va = a.strategy.vagas_livres;
          vb = b.strategy.vagas_livres;
          break;
        case "avg_occupancy":
          va = a.schedule.avg_occupancy;
          vb = b.schedule.avg_occupancy;
          break;
      }
      if (va === null || va === undefined) return sortDir === "asc" ? 1 : -1;
      if (vb === null || vb === undefined) return sortDir === "asc" ? -1 : 1;
      return sortDir === "asc" ? va - vb : vb - va;
    });
    return out;
  }, [rows, specialty, unit, doctor, search, sortBy, sortDir]);

  const summary = useMemo(() => {
    const total = filteredRows.length;
    const highOcc = filteredRows.filter((r) => (r.strategy.ocupacao || 0) >= 0.75).length;
    const lowOcc = filteredRows.filter((r) => (r.strategy.ocupacao || 0) < 0.50).length;
    const lowHours = filteredRows.filter((r) => (r.schedule.total_h_semana || 999) < 10).length;
    return { total, highOcc, lowOcc, lowHours };
  }, [filteredRows]);

  if (loading) {
    return (
      <div className="p-6 flex items-center gap-2 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin" />
        Loading schedule vs strategy...
      </div>
    );
  }

  if (error || !rows.length) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <h1 className="text-2xl font-bold text-slate-900">Schedules vs Strategy</h1>
        <p className="text-slate-500 mt-2">
          {error || "No data available. Upload a strategy workbook and a doctor schedules file to see the cross-reference."}
        </p>
      </div>
    );
  }

  const body = (
    <>
      {!embedded && (
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-slate-900">Schedules vs Strategy</h1>
          <p className="text-slate-500 text-sm mt-1">
            Cruzamento de horários reais (Outubro) com dados estratégicos por médico/unidade/especialidade.
          </p>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-slate-500 text-xs mb-1">Médicos visíveis</div>
          <div className="text-2xl font-bold text-slate-900">{summary.total}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-slate-500 text-xs mb-1 flex items-center gap-1"><ArrowUpRight className="w-3 h-3" /> Ocupação ≥75%</div>
          <div className="text-2xl font-bold text-emerald-700">{summary.highOcc}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-slate-500 text-xs mb-1 flex items-center gap-1"><ArrowDownRight className="w-3 h-3" /> Ocupação &lt;50%</div>
          <div className="text-2xl font-bold text-red-700">{summary.lowOcc}</div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="text-slate-500 text-xs mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Horas &lt;10h</div>
          <div className="text-2xl font-bold text-amber-700">{summary.lowHours}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 flex items-center gap-1"><Stethoscope className="w-3 h-3" /> Specialty</label>
            <select
              value={specialty}
              onChange={(e) => setSpecialty(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-trofa-700 min-w-[160px]"
            >
              <option value="">All</option>
              {filters.specialties.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 flex items-center gap-1"><Building2 className="w-3 h-3" /> Unit</label>
            <select
              value={unit}
              onChange={(e) => setUnit(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-trofa-700 min-w-[160px]"
            >
              <option value="">All</option>
              {filters.units.map((u) => <option key={u} value={u}>{u}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 flex items-center gap-1"><UserCircle className="w-3 h-3" /> Doctor</label>
            <select
              value={doctor}
              onChange={(e) => setDoctor(e.target.value)}
              className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-trofa-700 min-w-[200px]"
            >
              <option value="">All</option>
              {filters.doctors.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-xs text-slate-500 flex items-center gap-1"><Search className="w-3 h-3" /> Search</label>
            <div className="relative">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="doctor, unit, diagnosis..."
                className="border border-slate-300 rounded-lg pl-3 pr-8 py-2 text-sm focus:ring-2 focus:ring-trofa-700 w-[220px]"
              />
              {search && (
                <button onClick={() => setSearch("")} className="absolute right-2 top-1.5 text-slate-400 hover:text-slate-600">
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1 ml-auto">
            <label className="text-xs text-slate-500 flex items-center gap-1"><Filter className="w-3 h-3" /> Sort</label>
            <div className="flex items-center gap-1">
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as any)}
                className="border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-trofa-700"
              >
                <option value="ocupacao">Strategy occupancy</option>
                <option value="avg_occupancy">Schedule occupancy</option>
                <option value="horas">Real hours/week</option>
                <option value="consultas">Consultations</option>
                <option value="vagas_livres">Open slots</option>
              </select>
              <button
                onClick={() => setSortDir((d) => d === "asc" ? "desc" : "asc")}
                className="border border-slate-300 rounded-lg px-2 py-2 text-sm hover:bg-slate-50"
                title="Toggle sort direction"
              >
                {sortDir === "asc" ? "↑" : "↓"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Doctor</th>
                <th className="text-left px-4 py-3 font-medium">Unit</th>
                <th className="text-left px-4 py-3 font-medium">Specialty</th>
                <th className="text-right px-4 py-3 font-medium">Real h/sem</th>
                <th className="text-right px-4 py-3 font-medium">Dias</th>
                <th className="text-left px-4 py-3 font-medium">Horário</th>
                <th className="text-right px-4 py-3 font-medium">Avg occ (sched)</th>
                <th className="text-right px-4 py-3 font-medium">Consultas</th>
                <th className="text-right px-4 py-3 font-medium">Vagas livres</th>
                <th className="text-right px-4 py-3 font-medium">Ocupação (strat)</th>
                <th className="text-left px-4 py-3 font-medium">Diagnosis</th>
                <th className="text-center px-4 py-3 font-medium">Alerts</th>
              </tr>
            </thead>
            <tbody>
              {filteredRows.map((r, idx) => {
                const occ = r.strategy.ocupacao;
                const lowHours = (r.schedule.total_h_semana || 999) < 10;
                return (
                  <tr
                    key={`${r.specialty}-${r.unit}-${r.doctor}-${idx}`}
                    onClick={() => setSelectedRow(r)}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                  >
                    <td className="px-4 py-3 font-medium text-slate-800">{r.doctor}</td>
                    <td className="px-4 py-3 text-slate-600">{r.unit}</td>
                    <td className="px-4 py-3 text-slate-600">{r.specialty}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={lowHours ? "text-amber-700 font-semibold" : "text-slate-700"}>{fmtHours(r.schedule.total_h_semana)}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-600">{r.schedule.dias ?? "—"}</td>
                    <td className="px-4 py-3 text-slate-600 max-w-[180px] truncate" title={r.schedule.schedules || ""}>{r.schedule.schedules || "—"}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={occupancyColor(r.schedule.avg_occupancy)}>{fmtPct(r.schedule.avg_occupancy)}</span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">{fmtNum(r.strategy.consultas)}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{fmtNum(r.strategy.vagas_livres)}</td>
                    <td className="px-4 py-3 text-right">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs ${occupancyBadge(occ)}`}>
                        {fmtPct(occ)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 max-w-[180px] truncate" title={r.strategy.diagnosis || ""}>{r.strategy.diagnosis || "—"}</td>
                    <td className="px-4 py-3 text-center">
                      <div className="flex justify-center gap-1">
                        {(r.strategy.ocupacao || 0) >= 0.75 && <span className="w-2 h-2 rounded-full bg-emerald-500" title="High occupancy" />}
                        {(r.strategy.ocupacao || 0) < 0.50 && <span className="w-2 h-2 rounded-full bg-red-500" title="Low occupancy" />}
                        {lowHours && <span className="w-2 h-2 rounded-full bg-amber-500" title="Low hours" />}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Detail panel */}
      {selectedRow && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto p-6">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h2 className="text-lg font-bold text-slate-900">{selectedRow.doctor}</h2>
                <p className="text-sm text-slate-500">{selectedRow.unit} · {selectedRow.specialty}</p>
              </div>
              <button onClick={() => setSelectedRow(null)} className="text-slate-400 hover:text-slate-600">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">Real hours/week</div>
                <div className="text-lg font-semibold text-slate-800">{fmtHours(selectedRow.schedule.total_h_semana)}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">Strategy hours/week</div>
                <div className="text-lg font-semibold text-slate-800">{fmtHours(selectedRow.strategy.hours_per_week)}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">Consultations / vagas</div>
                <div className="text-lg font-semibold text-slate-800">{fmtNum(selectedRow.strategy.consultas)} / {fmtNum(selectedRow.strategy.vagas)}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">Open slots</div>
                <div className="text-lg font-semibold text-slate-800">{fmtNum(selectedRow.strategy.vagas_livres)}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">Schedule occupancy</div>
                <div className={`text-lg font-semibold ${occupancyColor(selectedRow.schedule.avg_occupancy)}`}>{fmtPct(selectedRow.schedule.avg_occupancy)}</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-3">
                <div className="text-xs text-slate-500 mb-1">Strategy occupancy</div>
                <div className={`text-lg font-semibold ${occupancyColor(selectedRow.strategy.ocupacao)}`}>{fmtPct(selectedRow.strategy.ocupacao)}</div>
              </div>
            </div>

            <div className="space-y-3">
              <div>
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Calendar className="w-3 h-3" /> Weekdays</div>
                <div className="text-sm text-slate-800">{selectedRow.schedule.weekdays || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Schedule blocks</div>
                <div className="text-sm text-slate-800">{selectedRow.schedule.schedules || "—"}</div>
              </div>
              {selectedRow.strategy.diagnosis && (
                <div>
                  <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><Activity className="w-3 h-3" /> Diagnosis</div>
                  <div className="text-sm text-slate-800">{selectedRow.strategy.diagnosis}</div>
                </div>
              )}
              {selectedRow.rules.occupancy_rule && (
                <div className="bg-slate-50 rounded-lg p-3">
                  <div className="text-xs text-slate-500 mb-1 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Occupancy rule</div>
                  <div className="text-sm text-slate-800">{selectedRow.rules.occupancy_rule}</div>
                </div>
              )}
              {selectedRow.rules.low_hours_rule && (
                <div className="bg-amber-50 rounded-lg p-3">
                  <div className="text-xs text-amber-700 mb-1 flex items-center gap-1"><Clock className="w-3 h-3" /> Low-hours rule</div>
                  <div className="text-sm text-amber-900">{selectedRow.rules.low_hours_rule}</div>
                </div>
              )}
              {selectedRow.strategy.conversion_rate !== null && selectedRow.strategy.conversion_rate !== undefined && (
                <div>
                  <div className="text-xs text-slate-500 mb-1">Surgery conversion rate</div>
                  <div className="text-sm text-slate-800">{fmtPct(selectedRow.strategy.conversion_rate)}</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );

  if (embedded) return body;
  return <div className="p-6 max-w-7xl mx-auto">{body}</div>;
}
